import React, { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { useLiveQuery } from "dexie-react-hooks";
import { CircularProgress, Typography, Box, Alert } from "@mui/material";

import { useDocumentActionsStatus } from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import { useRedirectIfUnauthenticated } from "utils/azureAD/useRedirectIfUnauthenticated";
import { IHomePageInfo, ISetting } from "utils/DexieDBManager/dexieDB.models";
import { getAllHomePageInfo, getSettingByKey } from "utils/DexieDBManager";
import { useGetHomePageInfo } from "./apis/useGetHomePageInfo";
import HomePageDocumentCards from "./HomePageDocumentCards";
import { useSettings } from "SettingsPage/Settings.Context";
import { HomePageInfoKeys } from "./homePage.service";
import { RESOURCES_KEYS } from "utils/i18n/resources";
import { computeErrorMessage } from "utils/utils";
import ErrorBoundary from "utils/ErrorBoundary";
import { isPwa } from "utils/pwa";

import {
  getSyncDispatchItemsAsync,
  getSynchronizationItemsAsync,
} from "PdfDocumentGrid/pdfDocuments.service";

import {
  useSyncDocumentsWithDBStatus,
  SYNC_DOCUMENTS_WITH_DB_ACTIONS,
} from "PdfDocumentGrid/hooks/SyncDocumentWithDbStatus.context";

const defaultHomePageInfoKeys = {
  lastAddedApplicables: [],
  lastAddedNextApplicables: [],
  lastRead: [],
  mostRead: [],
};

const HomePage: React.FunctionComponent = () => {
  const { t: translate } = useTranslation();
  const { state: settings } = useSettings();
  const { accounts } = useRedirectIfUnauthenticated();
  const [isDispatching, setIsDispatching] = useState<boolean>(false);

  const { state: syncDocsWithDbState, dispatch: syncDocsWithDbDispatch } = useSyncDocumentsWithDBStatus();
  const { state: documentActionsState, dispatch: dispatchDocumentActions } = useDocumentActionsStatus();
  const { data: homePageInfoFromServer, isLoading: isGetHomePageInfoLoading } = useGetHomePageInfo();

  const lastSynchroDate = useLiveQuery<ISetting | undefined>(() => getSettingByKey("lastSynchroDate"));
  const homePageInfoFromDb = useLiveQuery<IHomePageInfo[]>(() => getAllHomePageInfo());

  const isOnline = navigator.onLine;
  const username = accounts[0]?.username;

  useQuery(
    ["synchronizationItems", {username}],
    () => getSynchronizationItemsAsync(username, {hasToSyncDocuments: syncDocsWithDbState.SyncDocumentsWithDBEnabled}),
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnReconnect: false,
      enabled: isOnline && syncDocsWithDbState.SyncDocumentsWithDBEnabled && Boolean(username),
      onSuccess: () => syncDocsWithDbDispatch({ type: SYNC_DOCUMENTS_WITH_DB_ACTIONS.DISABLE })
    }
  );


  useQuery(
    ["syncDispatchItems", {username}],
    () => getSyncDispatchItemsAsync(username).then(() => setIsDispatching(!isDispatching)),
    {
      refetchOnReconnect: false,
      enabled: isOnline && settings.versionsApiHasBeenCalled && Boolean(lastSynchroDate?.value) && Boolean(username),
    }
  );

  const homePageInfoData = isPwa && !isOnline ?
                            homePageInfoFromDb?.[0] :
                            homePageInfoFromServer?.responseData;

  const isDataLoading =  (isPwa && !isOnline  && !homePageInfoData) || (isOnline && isGetHomePageInfoLoading && !homePageInfoData);

  React.useEffect(() => {
    if(isDataLoading) {
      return;
    }

    if (homePageInfoData) {
      Object.keys(homePageInfoData).forEach((key) => {
        homePageInfoData?.[key as HomePageInfoKeys].forEach(
          (documentVersion) => {
            const isInitialized = documentActionsState.documentActionsStatus.get(documentVersion.parentDocument.id)?.isInitialized;
            if (!isInitialized) {
              dispatchDocumentActions({
                type: "init",
                initialDocumentStatus: {
                  isAckRequired: documentVersion.uds.isAck,
                  isStarredDocument: documentVersion.uds.isFav,
                  isUnread: !Boolean(documentVersion.uds.lastRead),
                  parentDocumentId: documentVersion.parentDocument.id,
                  documentVersionType: documentVersion.applicableStateName,
                },
              });
            }
          }
        );
      });
    }
  }, [dispatchDocumentActions, homePageInfoData, documentActionsState.documentActionsStatus, isDataLoading]);

  return (
    <ErrorBoundary title={translate(RESOURCES_KEYS.ERROR_BOUNDARY_TITLE, {page: translate(RESOURCES_KEYS.MENU_HOME)})}>
      {isDataLoading ? (
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : homePageInfoFromServer?.error ? (
        <Alert sx={{ margin: "1rem 0" }} severity="error">
          {translate(computeErrorMessage(homePageInfoFromServer?.error))}
        </Alert>
      ) : (
        <>
          {Object.keys(defaultHomePageInfoKeys).map((key) => (
            <Box key={key} sx={{ margin: "0 0.5rem 2rem" }}>
              <Typography variant="h3" sx={{ margin: "2rem 0.5rem" }}>
                {translate(key)}
              </Typography>
              <HomePageDocumentCards documentVersionsToDisplay={homePageInfoData?.[key as HomePageInfoKeys] ?? []}/>
            </Box>
          ))}
        </>
      )}
    </ErrorBoundary>
  );
};

export default HomePage;
